
import { Button } from "@/components/ui/button";
import { Share } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { 
  generateQuotationUrl,
  generateQuotationMessage,
  formatWhatsAppNumber,
  openWhatsApp,
  isValidQuotationCode
} from "@/utils/quotation-utils";

interface ShareWhatsAppButtonProps {
  quotation: any;
}

export function ShareWhatsAppButton({ quotation }: ShareWhatsAppButtonProps) {
  const { toast } = useToast();

  const handleShare = () => {
    try {
      console.log("Sharing quotation via WhatsApp, data:", quotation);
      
      // Verify quotation has a valid code
      if (!quotation || !isValidQuotationCode(quotation.code)) {
        console.error('Quotation without valid code', quotation);
        toast({
          title: "Erro ao compartilhar",
          description: "Este orçamento não possui um código válido.",
          variant: "destructive",
        });
        return;
      }
      
      if (!quotation.customer) {
        console.error('Quotation without associated customer', quotation);
        toast({
          title: "Erro ao compartilhar",
          description: "Nenhum cliente associado ao orçamento.",
          variant: "destructive",
        });
        return;
      }
      
      const phone = quotation.customer.whatsapp || quotation.customer.phone;
      if (!phone) {
        console.error('Customer without contact number', quotation.customer);
        toast({
          title: "Erro ao compartilhar",
          description: "Cliente sem número de WhatsApp/telefone registrado.",
          variant: "destructive",
        });
        return;
      }
      
      // Generate the public URL for the quotation
      const url = generateQuotationUrl(quotation.code);
      console.log("Generated URL for sharing:", url);
      
      if (!url) {
        toast({
          title: "Erro ao compartilhar",
          description: "Não foi possível gerar o link do orçamento.",
          variant: "destructive",
        });
        return;
      }
      
      const formattedPhone = formatWhatsAppNumber(phone);
      const message = generateQuotationMessage(quotation.customer.name, url);
      
      // Debug logs
      console.log("Phone number:", phone);
      console.log("Formatted phone:", formattedPhone);
      console.log("Message:", message);
      
      // Clear service worker cache before sharing
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_QUOTATIONS_CACHE'
        });
      }
      
      const success = openWhatsApp(formattedPhone, message);
      
      if (success) {
        toast({
          title: "Link compartilhado",
          description: "WhatsApp aberto com o link do orçamento.",
        });
      } else {
        toast({
          title: "Erro ao abrir WhatsApp",
          description: "Não foi possível abrir o WhatsApp. Verifique o número do cliente.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sharing via WhatsApp:", error);
      toast({
        title: "Erro ao compartilhar",
        description: "Ocorreu um erro ao tentar compartilhar via WhatsApp.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleShare}
      className="hover:bg-[#0073B7] hover:text-white"
      title="Compartilhar via WhatsApp"
    >
      <Share className="h-4 w-4" />
    </Button>
  );
}
